import React, { useState } from 'react';
import { recordPayment, sendInvoice, deletePayment, deleteInvoice } from '../utils/api';
import { formatDate } from '../utils/date';
import SendInvoiceModal from './SendInvoiceModal';
import RecordPaymentModal from './RecordPaymentModal';
import './InvoiceDetailModal.css';
import './Invoices.css';

const InvoiceDetailModal = ({ invoice, onClose, onPaymentRecorded, onInvoiceDeleted, canManage }) => {
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [isRecordPaymentModalOpen, setRecordPaymentModalOpen] = useState(false);

  if (!invoice) {
    return null;
  }

  const handleSendInvoice = () => {
    setIsSendModalOpen(true);
  };

  const closeSendModal = () => {
    setIsSendModalOpen(false);
  };

  const formatAmount = (amount) => {
    return ((amount || 0) / 100).toFixed(2);
  };

  const handleDeletePayment = async (paymentId) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      try {
        await deletePayment(paymentId);
        onPaymentRecorded(); // This will refresh the invoice details
      } catch (error) {
        console.error('Failed to delete payment:', error);
        // You might want to show an error message to the user
      }
    }
  };

  const handleDeleteInvoice = async () => {
    if (window.confirm('Are you sure you want to delete this invoice? This will also delete all payments associated with this invoice')) {
      try {
        await deleteInvoice(invoice.id);
        onInvoiceDeleted(); // This will refresh the invoice list
        onClose(); // Close the modal
      } catch (error) {
        console.error('Failed to delete invoice:', error);
        // You might want to show an error message to the user
      }
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h2>Invoice Details</h2>
        <div className="invoice-details-grid">
          <div className="invoice-details-section">
            <h3>{invoice.name}</h3>
            <div className="detail-item"><h4>{invoice.student.first_name} {invoice.student.last_name}</h4></div>
            <div className="detail-item">{invoice.student.email}</div>
           {invoice.description && <div className="detail-item"><strong>Description:</strong> {invoice.description}</div>}
            <div className="detail-item"><strong>Issue Date:</strong> {formatDate(invoice.issue_date)}</div>
            <div className="detail-item"><strong>Due Date:</strong> {formatDate(invoice.due_date)}</div>
          </div>
          <div className="invoice-details-section">
            <h3>Fees & Discounts</h3>
            {invoice.invoice_fees.invoice_fees.map(fee => (
              <div key={fee.id} className="detail-item">
                <span>{fee.name} ({fee.quantity} x ${formatAmount(fee.amount)})</span>
                <span>${formatAmount(fee.amount * fee.quantity)}</span>
              </div>
            ))}
            {invoice.invoice_discounts.invoice_discounts.map(discount => (
              <div key={discount.id} className="detail-item">
                <span>
                  {discount.name}{' '}
                  {discount.type === 'Fixed'
                    ? `(-$${formatAmount(discount.amount)})`
                    : `(${discount.amount / 100}%)`}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="amounts-section">
          <div className="amount-item">
            <strong>Total Fees:</strong>
            <span>${formatAmount(invoice.invoice_fees.total_amount)}</span>
          </div>
          <div className="amount-item">
            <strong>Total Discounts:</strong>
            <span>-${formatAmount(invoice.invoice_discounts.total_amount)}</span>
          </div>
          <div className="amount-item">
            <strong className='total-amount'>Total Amount:</strong>
            <span className='total-amount'>${formatAmount(invoice.total_amount)}</span>
          </div>
          <div className="amount-item">
            <strong className='paid-amount'>Paid Amount:</strong>
            <span className='paid-amount'>${formatAmount(invoice.paid_amount)}</span>
          </div>
          <div className="amount-item">
            <strong className='due-amount'>Due Amount:</strong>
            <span className='due-amount'>${formatAmount(invoice.due_amount)}</span>
          </div>
        </div>

        <h3>Payments</h3>
        <div className="items-container">
          {invoice.payments && invoice.payments.length > 0 ? (
            invoice.payments.map(payment => (
              <div key={payment.id} className="item-row">
                <span>
                  ${formatAmount(payment.amount)} - {formatDate(payment.date)}
                  {payment.supplier_name && ` - ${payment.supplier_name}`}
                  {payment.supplier_id && ` (${payment.supplier_id})`}
                </span>
                {canManage && <button onClick={() => handleDeletePayment(payment.id)} className="delete-button text-button">Delete</button>}
              </div>
            ))
          ) : (
            <p>No payments recorded.</p>
          )}
        </div>

        {invoice.payment_link && (
          <p><a href={invoice.payment_link} target="_blank" rel="noopener noreferrer">Payment Link</a></p>
        )}
        
        <div className='actions-centered'>
          <button onClick={onClose} className='cancel-button text-button'>Close</button>
          {canManage && <button onClick={handleDeleteInvoice} className='submit-button text-button'>Delete</button>}
          {canManage && <button onClick={handleSendInvoice} className='cancel-button text-button'>Send</button>}
          {canManage && <button onClick={() => setRecordPaymentModalOpen(true)} className='submit-button text-button'>Record Payment</button>}
        </div>

        {isSendModalOpen && <SendInvoiceModal isOpen={isSendModalOpen} onClose={closeSendModal} sendRequest={() => sendInvoice(invoice.id)} />}
        {isRecordPaymentModalOpen && <RecordPaymentModal invoiceId={invoice.id} isOpen={isRecordPaymentModalOpen} onClose={() => setRecordPaymentModalOpen(false)} onPaymentRecorded={onPaymentRecorded} />}
      </div>
    </div>
  );
};

export default InvoiceDetailModal;
